import {
  Constraint_Purchase_Threshold_Counting_Attribute,
  ReferralProgramConfigurationFragment,
  ReferralProgramConfigurationInput,
  SubBrand,
} from '@occc/graphql';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AwardType } from '@occc-app/offerHelpers/types';

import { ReferralProgramConfigurationValues } from './referral-program-configuration.types';
import { referralProgramConfigurationSchema } from './referral-program-configuration.schema';

export type MessagingTab = {
  id: string;
  label: string;
  index: number;
};

const createEmptyMarketingContent = (
  subBrandUuid: string | null,
): ReferralProgramConfigurationValues['marketingContents'][number] => ({
  subBrandUuid,
  senderInstructions: '',
  receiverInstructions: '',
  smsTemplate: '',
});

export const buildMessagingTabs = (subBrands: SubBrand[] | null): MessagingTab[] => {
  if (subBrands?.length) {
    return subBrands.map((subBrand, index) => ({
      id: `messages-${index}`,
      label: `${subBrand.name} Messages`,
      index,
    }));
  }

  return [{ id: 'messages-0', label: 'Messages', index: 0 }];
};

const buildDefaultMarketingContents = (
  subBrands: SubBrand[] | null,
): ReferralProgramConfigurationValues['marketingContents'] => {
  if (subBrands?.length) {
    return subBrands.map((subBrand) => createEmptyMarketingContent(subBrand.uuid));
  }

  return [createEmptyMarketingContent(null)];
};

export const mapMarketingContentsFromConfiguration = (params: {
  referralProgramConfiguration: ReferralProgramConfigurationFragment;
  subBrands: SubBrand[] | null;
}): ReferralProgramConfigurationValues['marketingContents'] => {
  const { referralProgramConfiguration, subBrands } = params;
  const { marketingContents, senderConfiguration, receiverConfiguration } =
    referralProgramConfiguration;

  return buildDefaultMarketingContents(subBrands).map((defaultEntry) => {
    const fromApi = marketingContents.find(
      (content) => (content.subBrandUuid ?? null) === defaultEntry.subBrandUuid,
    );

    if (fromApi) {
      return {
        subBrandUuid: fromApi.subBrandUuid ?? null,
        senderInstructions: fromApi.senderInstructions,
        receiverInstructions: fromApi.receiverInstructions,
        smsTemplate: fromApi.smsTemplate,
      };
    }

    if (defaultEntry.subBrandUuid === null && !subBrands?.length) {
      return {
        subBrandUuid: null,
        senderInstructions: senderConfiguration.instructions,
        receiverInstructions: receiverConfiguration.instructions,
        smsTemplate: senderConfiguration.smsTemplate,
      };
    }

    return defaultEntry;
  });
};

export const createReferralProgramConfigurationInitialValues = (
  referralProgramConfiguration: ReferralProgramConfigurationFragment | null,
  subBrands: SubBrand[] | null = null,
): ReferralProgramConfigurationValues => {
  const initialValues: ReferralProgramConfigurationValues = {
    sender: {
      award: {
        type: AwardType.AwardNullable,
        points: null,
        voucheredOffer: null,
        voucheredOfferExpirationDays: null,
        voucherCount: 1,
      },
    },
    receiver: {
      award: {
        type: AwardType.AwardNullable,
        points: null,
        voucheredOffer: null,
        voucheredOfferExpirationDays: null,
        voucherCount: 1,
      },
    },
    qualifyingEvent: {
      type: 'transaction',
      loyaltyEventOffer: null,
      transaction: {
        productGroups: [],
        countProductsBy: Constraint_Purchase_Threshold_Counting_Attribute.MoneyAmount,
        purchaseThreshold: 1,
      },
    },
    marketingContents: buildDefaultMarketingContents(subBrands),
  };

  if (!referralProgramConfiguration) {
    return initialValues;
  }

  const { senderConfiguration, receiverConfiguration, qualifiedEvent } =
    referralProgramConfiguration;

  initialValues.marketingContents = mapMarketingContentsFromConfiguration({
    referralProgramConfiguration,
    subBrands,
  });

  // region Sender award
  if (senderConfiguration.award?.__typename === 'AwardPoints') {
    initialValues.sender.award = {
      type: AwardType.AwardPoints,
      points: senderConfiguration.award.points,
      voucheredOffer: null,
      voucheredOfferExpirationDays: null,
      voucherCount: 1,
    };
  }

  if (senderConfiguration.award?.__typename === 'AwardVouchers') {
    initialValues.sender.award = {
      type: AwardType.AwardVouchers,
      voucheredOffer: {
        id: senderConfiguration.award.voucheredOffer.id,
        name: senderConfiguration.award.voucheredOffer.name,
      },
      voucheredOfferExpirationDays: senderConfiguration.award.voucherExpirationDays || null,
      voucherCount: senderConfiguration.award.voucherCount,
      points: null,
    };
  }
  // endregion

  // region Receiver award
  if (receiverConfiguration.award?.__typename === 'AwardPoints') {
    initialValues.receiver.award = {
      type: AwardType.AwardPoints,
      points: receiverConfiguration.award.points,
      voucheredOffer: null,
      voucheredOfferExpirationDays: null,
      voucherCount: 1,
    };
  }

  if (receiverConfiguration.award?.__typename === 'AwardVouchers') {
    initialValues.receiver.award = {
      type: AwardType.AwardVouchers,
      voucheredOffer: {
        id: receiverConfiguration.award.voucheredOffer.id,
        name: receiverConfiguration.award.voucheredOffer.name,
      },
      voucheredOfferExpirationDays: receiverConfiguration.award.voucherExpirationDays || null,
      voucherCount: receiverConfiguration.award.voucherCount,
      points: null,
    };
  }
  // endregion

  // region Qualified Event
  if (qualifiedEvent.__typename === 'QualifiedEventTransaction') {
    initialValues.qualifyingEvent = {
      type: 'transaction',
      transaction: {
        countProductsBy: qualifiedEvent.countProductsBy,
        purchaseThreshold: qualifiedEvent.purchaseThreshold,
        productGroups: qualifiedEvent.eligibleProductGroups.map((productGroup) => ({
          id: productGroup.id,
          name: productGroup.name,
        })),
      },
      loyaltyEventOffer: null,
    };
  }

  if (qualifiedEvent.__typename === 'QualifiedEventLoyaltyEvent') {
    initialValues.qualifyingEvent = {
      type: 'loyaltyEvent',
      loyaltyEventOffer: {
        id: qualifiedEvent.loyaltyEventOffer.id,
        name: qualifiedEvent.loyaltyEventOffer.name,
      },
      transaction: null,
    };

    // Default value for transaction is required
    // FinalForm and validation does not work when qualifiedEvent type is changed from transaction to loyaltyEvent
    // Alternative solution is to set onTypeChange to set transaction value if missing
    // @ts-ignore
    initialValues.qualifyingEvent.transaction = {
      countProductsBy: Constraint_Purchase_Threshold_Counting_Attribute.MoneyAmount,
      purchaseThreshold: 1,
      productGroups: [],
    } satisfies ReferralProgramConfigurationValues['qualifyingEvent']['transaction'];
  }
  // endregion

  return initialValues;
};

export const createConfigureReferralProgramInput = (params: {
  chainId: string;
  values: ReferralProgramConfigurationValues;
}): ReferralProgramConfigurationInput => {
  const { chainId, values } = params;
  const processedValues = referralProgramConfigurationSchema.parse(values);
  const { sender, receiver, qualifyingEvent, marketingContents } = processedValues;

  const input: ReferralProgramConfigurationInput = {
    chainId,
    senderConfiguration: {
      awardPoints: null,
      awardVouchers: null,
    },
    receiverConfiguration: {
      awardPoints: null,
      awardVouchers: null,
    },
    qualifiedEvent: {
      loyaltyEvent: null,
      transaction: null,
    },
    marketingContents: marketingContents.map((content) => ({
      subBrandUuid: content.subBrandUuid ?? undefined,
      senderInstructions: content.senderInstructions,
      receiverInstructions: content.receiverInstructions,
      smsTemplate: content.smsTemplate,
    })),
  };

  // region Sender award
  if (sender.award.type === AwardType.AwardPoints) {
    input.senderConfiguration.awardPoints = { points: sender.award.points };
  }

  if (sender.award.type === AwardType.AwardVouchers) {
    input.senderConfiguration.awardVouchers = {
      voucherCount: sender.award.voucherCount,
      voucherExpirationDays: sender.award.voucheredOfferExpirationDays || null,
      voucheredOfferId: sender.award.voucheredOffer.id,
    };
  }
  // endregion

  // region Receiver award
  if (receiver.award.type === AwardType.AwardPoints) {
    input.receiverConfiguration.awardPoints = { points: receiver.award.points };
  }

  if (receiver.award.type === AwardType.AwardVouchers) {
    input.receiverConfiguration.awardVouchers = {
      voucherCount: receiver.award.voucherCount,
      voucherExpirationDays: receiver.award.voucheredOfferExpirationDays || null,
      voucheredOfferId: receiver.award.voucheredOffer.id,
    };
  }
  // endregion

  // region Qualified Event
  if (qualifyingEvent.type === 'transaction') {
    input.qualifiedEvent.transaction = {
      countProductsBy: qualifyingEvent.transaction.countProductsBy,
      purchaseThreshold: qualifyingEvent.transaction.purchaseThreshold,
      eligibleProductGroups: qualifyingEvent.transaction.productGroups.map(
        (productGroup) => productGroup.id,
      ),
    };
  }

  if (qualifyingEvent.type === 'loyaltyEvent') {
    input.qualifiedEvent.loyaltyEvent = {
      loyaltyEventOffer: qualifyingEvent.loyaltyEventOffer.id,
    };
  }
  // endregion

  return input;
};
